import { parseGermanQuantity, parseGermanUserNumber } from "@/lib/formatting";
import {
  COMMISSION_RATE,
  COMMISSION_RATE_PERCENT,
  MIN_PROVISION_EUR_NET,
} from "@/lib/platform/constants";

export interface ProvisionEstimateInput {
  type: "okopunkte" | "ausgleichsflaechen";
  quantityRaw: string;
  maxPricePerPointRaw: string;
  marketAskMinEur: number | null;
}

export interface ProvisionEstimate {
  quantity: number;
  pricePerPointEur: number;
  priceSource: "user" | "market";
  estimatedDealValueEur: number;
  calculatedProvisionEur: number;
  effectiveRatePercent: number;
  minProvisionApplies: true;
  /** Snapshot of the rate the calc used — keeps the renderer self-describing. */
  commissionRatePercent: number;
  /** Snapshot of the floor the calc used — same reason. */
  minProvisionEurNet: number;
}

/**
 * Returns a provision estimate when the Mindestprovision would apply to a
 * buyer's Anfrage — i.e. quantity × price × COMMISSION_RATE < MIN_PROVISION_EUR_NET.
 * Returns null when it cannot compute (missing inputs, no price anchor) OR
 * when the calculated rate already exceeds the floor — the renderer treats
 * both as "nothing to show".
 *
 * v1 scope: only Ökopunkte. Ausgleichsflächen prices are per-hectare with
 * different units and need a separate anchor.
 */
export function getProvisionEstimate(input: ProvisionEstimateInput): ProvisionEstimate | null {
  if (input.type !== "okopunkte") return null;

  const quantity = parseGermanQuantity(input.quantityRaw, "integer");
  if (!Number.isFinite(quantity) || quantity <= 0) return null;

  const userPrice = parseGermanUserNumber(input.maxPricePerPointRaw);
  const marketPrice = input.marketAskMinEur ?? 0;
  const pricePerPointEur = userPrice > 0 ? userPrice : marketPrice;
  if (pricePerPointEur <= 0) return null;

  const estimatedDealValueEur = quantity * pricePerPointEur;
  const calculatedProvisionEur = estimatedDealValueEur * COMMISSION_RATE;
  if (calculatedProvisionEur >= MIN_PROVISION_EUR_NET) return null;

  return {
    quantity,
    pricePerPointEur,
    priceSource: userPrice > 0 ? "user" : "market",
    estimatedDealValueEur,
    calculatedProvisionEur,
    effectiveRatePercent: Math.round((MIN_PROVISION_EUR_NET / estimatedDealValueEur) * 100),
    minProvisionApplies: true,
    commissionRatePercent: COMMISSION_RATE_PERCENT,
    minProvisionEurNet: MIN_PROVISION_EUR_NET,
  };
}

/**
 * Estimated commission in EUR for ads / conversion-tracking. Mirrors the
 * Mindestprovision rule: `max(qty × price × COMMISSION_RATE, MIN_PROVISION_EUR_NET)`.
 * Returns null when inputs aren't usable so the caller can pass `undefined`
 * to the tracker.
 */
export function estimateCommissionEur(quantity: number, pricePerUnit: number): number | null {
  if (!Number.isFinite(quantity) || quantity <= 0) return null;
  if (!Number.isFinite(pricePerUnit) || pricePerUnit <= 0) return null;
  const calculated = quantity * pricePerUnit * COMMISSION_RATE;
  return Math.max(calculated, MIN_PROVISION_EUR_NET);
}
